import { CButton, CButtonGroup, CCard, CCardBody, CCol, CRow } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { useTranslation } from "react-i18next";
import { cilBook, cilChevronRight, cilCommentBubble } from "@coreui/icons";
import { AltSymbolIcon, DirectionIcon, RiskIcon } from "@scryerlabs/ui";
import { TradingStrategyDirection } from "../../api/hooks/useTradingStrategies";
import { Key } from "./HelpExampleKey";

/**
 * A worked example of a strategy's page, keyed 1-11 so the article can name each part.
 *
 * Built from literals for the same reasons as HelpExampleCard: the real page navigates, mutates a
 * cart and resolves its timeframe, asset class and risk label through the reference API, and a help page can be the
 * first page of a session with none of that loaded.
 *
 * Scaled down rather than reproduced pixel for pixel. The article claims what each region is and
 * what the tabs are called, so the labels are the part that has to match; the chart is a stand-in.
 */

const FIGURES: [string, string, string][] = [
  ["+232%/yr", "Avg annual profit", "text-success"],
  ["-11.4%", "Max real drawdown", "text-danger"],
  ["68.1%", "Win rate", "text-success"],
  ["+1,044%", "Best year", "text-success"],
  ["-2.6%", "Worst year", "text-danger"],
  ["10 of 11 yrs", "Profitable years", "text-success"],
];

const TABS = [
  "Equity Curve",
  "Annual Returns",
  "Candles",
  "Backtested Stats",
  "Annualised Returns",
  "Trades",
];

const COLUMN_LINKS: [string[], string][] = [
  [cilCommentBubble, "Ask a question"],
  [cilBook, "How to read this page"],
];

/** The key that sits on each tab, in the order TABS declares them. */
const TAB_KEYS = [5, 6, 7, 8, 9, 10];

function Fact({
  label,
  children,
  keyNumber,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  keyNumber?: number;
  mono?: boolean;
}) {
  return (
    <div>
      <div
        className="text-body-secondary text-uppercase fw-semibold"
        style={{ fontSize: "0.6rem", letterSpacing: "0.08em" }}
      >
        {label}
        {keyNumber !== undefined && <Key n={keyNumber} />}
      </div>
      <div className={mono ? "font-monospace" : "fw-semibold"} style={{ fontSize: "0.8rem" }}>
        {children}
      </div>
    </div>
  );
}

/** Laid out with the real page's own classes, so the example lines up the way the page does. */
export function HelpExampleStrategyPage() {
  const { t } = useTranslation("tradingStrategies");

  return (
    <figure className="help-example-card help-example-page">
      <CCard>
        <CCardBody>
          <CRow xs={{ gutter: 3 }} className="pb-3 border-bottom">
            <CCol md={8}>
              <h3 className="mb-1" style={{ fontSize: "1.35rem" }}>
                Example Strategy
                <Key n={1} />
              </h3>
              <p className="text-body-secondary mb-0" style={{ fontSize: "0.85rem" }}>
                A made-up strategy, here only to be pointed at.
              </p>

              <div className="mt-3 pt-3 border-top strategy-facts">
                <Fact label="Charted on" keyNumber={2} mono>
                  MSTR
                </Fact>
                <Fact label="Direction">
                  <span className="d-inline-flex align-items-center gap-1">
                    <DirectionIcon
                      direction={TradingStrategyDirection.LongAndShort}
                      label="Trades long and short"
                      size={16}
                    />
                    Long &amp; short
                  </span>
                </Fact>
                <Fact label="Traded through">
                  <span className="d-inline-flex align-items-center gap-1">
                    <AltSymbolIcon label="Alt-Symbol Boost" size={16} />
                    <span className="font-monospace">
                      <span style={{ color: "var(--sl-positive)" }}>MSTX</span>
                      <span className="text-body-secondary fw-normal"> / </span>
                      <span style={{ color: "var(--sl-negative)" }}>SMST</span>
                    </span>
                  </span>
                </Fact>
                <Fact label="Risk profile">
                  <span className="d-inline-flex align-items-center gap-1">
                    <RiskIcon riskProfile={2} label="Balanced risk" size={16} />
                    Balanced
                  </span>
                </Fact>
                <Fact label="Timeframe">1D</Fact>
                <Fact label="Asset class">Technology</Fact>
              </div>

              <div className="help-example-figures">
                {FIGURES.map(([value, label, tone]) => (
                  <div key={label} className="help-example-figure">
                    <div className={`fw-bold ${tone}`} style={{ fontSize: "0.95rem", lineHeight: 1.2 }}>
                      {value}
                    </div>
                    <div
                      className="text-body-secondary text-uppercase"
                      style={{ fontSize: "0.52rem", letterSpacing: "0.05em" }}
                    >
                      {label}
                    </div>
                  </div>
                ))}
                <Key n={3} />
              </div>
            </CCol>

            <CCol md={4}>
              <div className="subscription-column d-flex flex-column gap-2">
                <div
                  className="text-body-secondary text-uppercase fw-semibold"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.08em" }}
                >
                  Billing cycle
                  <Key n={4} />
                </div>
                <CButtonGroup className="btn-group-pill" size="sm">
                  <CButton color="primary">Monthly</CButton>
                  <CButton color="primary" variant="outline">
                    Yearly · save 17%
                  </CButton>
                </CButtonGroup>
                <div className="fw-bold" style={{ fontSize: "1.5rem", lineHeight: 1.1 }}>
                  $59 <span className="fs-6 fw-normal text-body-secondary">/ month</span>
                </div>
                <CButton color="primary" size="sm">
                  Add to cart
                </CButton>
                <div className="strategy-column-links">
                  {COLUMN_LINKS.map(([icon, label]) => (
                    <div key={label} className="strategy-column-link" style={{ fontSize: "0.75rem", minHeight: "2rem" }}>
                      <span className="strategy-column-link-icon">
                        <CIcon icon={icon} size="sm" />
                      </span>
                      <span className="strategy-column-link-label">{label}</span>
                      <CIcon icon={cilChevronRight} size="sm" className="card-link-chevron" aria-hidden />
                    </div>
                  ))}
                </div>
              </div>
            </CCol>
          </CRow>

          <div className="help-example-tabs mt-3">
            {TABS.map((tab, i) => (
              <span key={tab} className={i === 0 ? "help-example-tab active" : "help-example-tab"}>
                {tab}
                <Key n={TAB_KEYS[i]} />
              </span>
            ))}
          </div>

          {/* A stand-in, not a rendered chart: the article names the tab, it does not describe the
              plot, and a real curve here would be another set of invented figures to keep honest. */}
          <div className="help-example-plot" aria-hidden>
            <svg viewBox="0 0 300 70" preserveAspectRatio="none" width="100%" height="70">
              <polyline
                points="0,66 30,64 60,61 90,55 120,58 150,40 180,33 210,22 240,18 270,10 300,6"
                fill="none"
                stroke="var(--sl-positive)"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>

          {/* The real page's wording, so the example cannot drift from it. */}
          <div className="text-body-secondary border-top pt-2 mt-3" style={{ fontSize: "0.6rem", lineHeight: 1.6 }}>
            {t("performanceNoteWithPeriod", { period: "Jan 2016 - Sep 2026", months: 128 })}{" "}
            <span className="text-primary">{t("performanceNoteLink")}</span>
            <Key n={11} />
          </div>
        </CCardBody>
      </CCard>

      <figcaption>
        An example. <strong>Not a real strategy</strong> — the name, the price and the figures are
        invented.
      </figcaption>
    </figure>
  );
}
